import * as _ from "lodash-es";
import moment from "moment-timezone";
import React, { Component, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import IOrder from "../../../../models/IOrder";
import ISupplierModel from "../../../../models/ISupplierModel";
import AddressDisplay from "../../AddressDisplay/AddressDisplay";
import OwCheckbox from "../../OwCheckbox/OwCheckbox";
import OwLabel from "../../OwLabel/OwLabel";
import OwTitle from "../../OwTitle/OwTitle";
import OwTranslate from "../../OwTranslate/OwTranslate";
import PackingListItemRow from "../../../shipments/ShipmentPackingList/PackingListItemRow/PackingListItemRow";
import UIService from "../../../../services/UIService";
import styled from "styled-components";
import { DocLogo } from "../../DocLogo/DocLogo";
import { WhiteLabelLogo } from "../../WhiteLabelLogo/WhiteLabelLogo";
import IOrder_Item from "../../../../models/IOrder_Item";
import OwSortHeader from "../../OwSortHeader/OwSortHeader";
import SortOrderEnum from "../../../../models/SortOrderEnum";
import SupplierContext from "../../../../contexts/SupplierContext";
import SupplierGuidEnum from "../../../../models/SupplierGuidEnum";
import "./OrderPackingListView.scss";

type Props = {
    order: IOrder;
    showItemPhoto: boolean;
    showOrderQty: boolean;
    showShipQty: boolean;
    showDropshipment: boolean;
    showPhone: boolean;
    showEmail: boolean;
    showSku: boolean;
    showUpc: boolean;
    showOrderDescription: boolean;
    hideSkuCol: boolean;
    showBillTo: boolean;
    showShipToPhone: boolean;
    showBillToPhone: boolean;
    showOrderUser: boolean;
    showItemLocation: boolean;
    showUom: boolean;
    inlineLogoName: boolean;
    hideBusinessName: boolean;
    sortCol: "item_no" | "sku" | "location";
    template: "STANDARD" | "MINIMAL";
};

const OrderPackingListView = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const vendorModel = props.order.Vendor;
    const supplierShipTo = supplierModel!.Ship_To;

    const [sortCol, setSortCol] = useState<string>(props.sortCol as string);
    const [sortDir, setSortDir] = useState(SortOrderEnum.ASC);
    const [thHeightFix, setThHeightFix] = useState<boolean>(props.showItemLocation);

    useEffect(() => {
        setThHeightFix(props.showItemLocation);
    }, [props.showItemLocation]);

    const updateSortFn = (_sortCol: string, _sortDir: SortOrderEnum) => {
        setSortCol(_sortCol);
        setSortDir(_sortDir);
    };

    const shipmentGroupShipTo = !_.isNil(props.order.Ship_To) ? props.order.Ship_To : vendorModel?.Ship_To;
    const shipmentGroupBillTo = vendorModel?.Bill_To;

    const OrderItemsParsed = _.map(props.order.Order_Items, (item) => {
        item.item_no = Number(item.item_no);
        return item;
    });

    const OrderItems = _.sortBy(OrderItemsParsed, ["item_no", "asc"]);
    // const OrderItems = _.sortBy(props.order.Order_Items, ["item_no", "asc"]);
    const orderModel = props.order;

    const orderDate =
        orderModel.confirmed_date || orderModel.submit_date
            ? moment(orderModel.confirmed_date || orderModel.submit_date).format("MM/DD/YYYY")
            : "";

    const estShipDate = moment(orderModel.estimated_shipping_at).format("MM/DD/YYYY");

    const cancelDate = moment(orderModel.cancel_date).format("MM/DD/YYYY");
    const shippingDate = moment(orderModel.ship_date).format("MM/DD/YYYY");
    const doNotShipBeforeDate = moment(orderModel.do_not_ship_before_date).format("MM/DD/YYYY");

    const orderNumsArr: string[] = [];
    const poNumsArr: string[] = [];
    const notes = orderModel.note || "";

    orderNumsArr.push(orderModel?.name || "");

    if (orderModel.po_no) {
        poNumsArr.push(orderModel.po_no);
    }

    const orderNums = orderNumsArr.join(",");
    const poNums = poNumsArr.join(",");

    let totalQty = 0;

    const sortOrderItemsFn = (itemA: IOrder_Item, itemB: IOrder_Item): number => {
        if (sortCol === "location") {
            return sortOrderLocationFn(itemA, itemB);
        } else {
            return sortOrderItemNoFn(itemA, itemB);
        }
    };

    const sortOrderItemNoFn = (itemA: IOrder_Item, itemB: IOrder_Item): number => {
        if (itemA.item_no < itemB.item_no) {
            return -1;
        } else if (itemA.item_no > itemB.item_no) {
            return 1;
        } else {
            return 0;
        }
    };

    const sortOrderLocationFn = (itemA: IOrder_Item, itemB: IOrder_Item): number => {
        if ((itemA.Catalog_Item!.row || "") < (itemB.Catalog_Item!.row || "")) {
            return -1;
        } else if ((itemA.Catalog_Item!.row || "") > (itemB.Catalog_Item!.row || "")) {
            return 1;
        } else {
            return 0;
        }
    };

    const orderItemRows = OrderItems.sort(sortOrderItemsFn).map((orderItem: any, index: number) => {
        totalQty += Number(orderItem.qty);

        return (
            <PackingListItemRow
                key={orderItem.guid}
                showItemPhoto={props.showItemPhoto}
                orderItem={orderItem}
                showOrderQty={props.showOrderQty}
                showShipQty={props.showShipQty}
                showDropshipment={props.showDropshipment}
                showUpc={props.showUpc}
                showSku={props.showSku}
                showLot={!!supplierModel.has_lots}
                hideSkuCol={props.hideSkuCol}
                showItemLocation={props.showItemLocation}
                order={orderModel}
                supplierModel={supplierModel}
                showUom={props.showUom}
            />
        );
    });

    let tableTheme = "";

    if (!UIService.isMainViewLight()) {
        tableTheme = "table-dark";
    }

    let hasBillToName = true;
    let hasShipToName = true;

    if (!shipmentGroupBillTo?.name?.length || !shipmentGroupBillTo?.company_name?.length) {
        let billToNameSame = false;
        let billToCompanyNameSame = false;

        if (shipmentGroupBillTo?.name === vendorModel?.name) {
            billToNameSame = true;
        }

        if (shipmentGroupBillTo?.company_name === vendorModel?.name) {
            billToCompanyNameSame = true;
        }

        if (!billToNameSame && !billToCompanyNameSame) {
            hasBillToName = false;
        }
    }

    if (!shipmentGroupShipTo?.name?.length || !shipmentGroupShipTo?.company_name?.length) {
        let shipToNameSame = false;
        let shipToCompanyNameSame = false;

        if (shipmentGroupShipTo?.name === vendorModel?.name) {
            shipToNameSame = true;
        }

        if (shipmentGroupShipTo?.company_name === vendorModel?.name) {
            shipToCompanyNameSame = true;
        }

        if (!shipToNameSame && !shipToCompanyNameSame) {
            hasShipToName = false;
        }

        if (props.order.client_id) {
            hasShipToName = true;
        }
    }

    const User = props.order.User!;

    const getShowBillTo = () => {
        if (props.showBillTo) {
            if (props.template === "STANDARD") {
                return true;
            }
        }

        return false;
    };

    const showDetailRow = () => {
        return props.template === "STANDARD";
    };

    return (
        <OrderPackingListViewDiv className={"OrderPackingListView " + props.template}>
            <div className={"top-row"}>
                <div className={"d-flex justify-content-between"}>
                    <div className={" p-1 " + (props.inlineLogoName ? "d-flex" : "")}>
                        {supplierModel.Doc_Logo ? (
                            <DocLogo maxWidth={props.template === "STANDARD" ? "20rem" : "10rem"} maxHeight={"8rem"} height={"auto"} />
                        ) : (
                            <WhiteLabelLogo maxWidth={"20rem"} />
                        )}
                        {props.hideBusinessName ? null : (
                            <div className={props.inlineLogoName ? "ms-4" : ""}>
                                <OwTitle disableTranslation={true} title={supplierModel.name} tag={"h5"} className={"mb-0"} />
                                <div>{supplierShipTo.address_line_one}</div>
                                <div>{supplierShipTo.address_line_two}</div>
                                <div>
                                    {supplierShipTo.city}
                                    {supplierShipTo.state?.length ? "," : ""} {supplierShipTo.state} {supplierShipTo.postal_code}
                                </div>
                                <div className={"mt-2"}>
                                    {supplierModel.has_user_detail_on_packing_list ? (
                                        <div>
                                            <b>{User.name}</b>
                                        </div>
                                    ) : null}
                                    <div className={props.showPhone && supplierModel.phone ? "" : "d-none"}>
                                        <b>
                                            <OwTranslate defaultMessage={"phone"} id={"phone"} />:
                                        </b>{" "}
                                        {supplierModel.has_user_detail_on_packing_list ? User.phone || "" : supplierModel.phone}
                                    </div>
                                    <div className={props.showEmail && supplierModel.email ? "" : "d-none"}>
                                        <b>
                                            <OwTranslate defaultMessage={"email"} id={"email"} />:
                                        </b>{" "}
                                        {supplierModel.has_user_detail_on_packing_list ? User.email || "" : supplierModel.email}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={"text-end align-items-end d-md-flex flex-column"}>
                        <OwTitle title={"packing_list"} tag={"h3"} isBold={true} />
                        <div>
                            <OwLabel title={"date"} postText={":"} /> {moment().format("MM/DD/YYYY")}
                        </div>
                        {props.order.allow_substitutions ? (
                            <p className={"mt-2"}>
                                <OwLabel title={"allow_substitutions"} />
                            </p>
                        ) : null}
                        {props.order.allow_backorders ? (
                            <p className={"mt-2"}>
                                <OwLabel title={"allow_backorders"} />
                            </p>
                        ) : null}
                        {props.order.is_local_pickup ? (
                            <p className={"mt-4"}>
                                <OwTitle title={"local_pickup"} tag={"h4"} />
                            </p>
                        ) : null}
                        {props.order.is_local_delivery ? (
                            <p className={"mt-4"}>
                                <OwTitle title={"local_delivery"} tag={"h4"} />
                            </p>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className={"row bill-to-ship-to-row"}>
                {getShowBillTo() ? (
                    <div
                        className={
                            (props.template === "STANDARD" ? "w-20r" : "") +
                            " col-auto " +
                            (supplierModel.guid === SupplierGuidEnum.VERMONT ? "d-none" : "")
                        }>
                        <div className={"black-box p-2 h-100"}>
                            <OwTitle leftIconName={"fal fa-building"} className={"underline"} title={"bill_to"} tag={"h5"} />
                            {!hasBillToName ? (
                                <OwTitle title={props.order.Vendor?.name || ""} tag={"h5"} disableTranslation={true} />
                            ) : null}
                            <AddressDisplay
                                address={shipmentGroupBillTo}
                                showCompanyName={true}
                                showName={true}
                                showPhone={props.showBillToPhone}
                                hideMobile={true}
                                hideFax={true}
                                hideEmail={true}
                                nameTag={"h5"}
                            />
                        </div>
                    </div>
                ) : null}

                <div
                    className={
                        (props.template === "STANDARD" ? "w-20r" : "") +
                        " col-auto " +
                        (supplierModel.guid === SupplierGuidEnum.VERMONT ? "" : "border-left-0")
                    }>
                    <div className={"black-box p-2 h-100 "}>
                        {props.order.is_local_pickup ? (
                            <OwTitle leftIconName={"fal fa-hand-holding-box"} title={"local_pickup"} tag={"h5"} className={"underline"} />
                        ) : (
                            <OwTitle
                                leftIconName={"fal fa-truck"}
                                title={props.order.is_local_delivery ? "deliver_to" : "ship_to"}
                                tag={"h5"}
                                className={"underline"}
                            />
                        )}
                        {!hasShipToName ? <OwTitle title={props.order.Vendor?.name || ""} tag={"h5"} disableTranslation={true} /> : null}
                        <AddressDisplay
                            address={shipmentGroupShipTo}
                            showCompanyName={!props.order.client_id}
                            showName={true}
                            showPhone={props.showShipToPhone}
                            hideEmail={true}
                            hideFax={true}
                            hideMobile={true}
                            nameTag={"h5"}
                        />
                    </div>
                </div>

                {props.template === "MINIMAL" ? (
                    <div className={"col-auto"}>
                        <table className={"table table-sm table-bordered w-100 " + tableTheme}>
                            <tbody>
                                <tr>
                                    <th>
                                        <strong>
                                            <OwLabel title={"order_date"} className={"mb-0"} />
                                        </strong>
                                    </th>
                                    <td>{orderDate}</td>
                                </tr>
                                {supplierModel?.shipping_lead_time ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"est_ship_date"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{estShipDate}</td>
                                    </tr>
                                ) : null}
                                {supplierModel?.has_order_cancel_date ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"cancel_date"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.cancel_date ? cancelDate : null}</td>
                                    </tr>
                                ) : null}
                                {!supplierModel?.has_est_ship_date ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"ship_date"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.ship_date ? shippingDate : null}</td>
                                    </tr>
                                ) : null}
                                {supplierModel?.has_do_not_ship_before_date ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"do_not_ship_before_date"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.do_not_ship_before_date ? doNotShipBeforeDate : null}</td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <th>
                                        <strong>
                                            <OwLabel title={"order_no"} className={"mb-0"} />
                                        </strong>
                                    </th>
                                    <td>{orderNums}</td>
                                </tr>
                                {supplierModel?.has_terms_on_packing_list ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"terms"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>
                                            <OwLabel title={props.order.Vendor?.Payment_Type?.name.toLowerCase()} noBottomMargin={true} />
                                        </td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <th>
                                        <strong>
                                            <OwLabel title={"po_no"} className={"mb-0"} />
                                        </strong>
                                    </th>
                                    <td>{poNums}</td>
                                </tr>
                                <tr>
                                    <th>
                                        <strong>
                                            <OwLabel title={"notes"} className={"mb-0"} />
                                        </strong>
                                    </th>
                                    <td>{notes}</td>
                                </tr>
                                {props.order.shipping_instructions ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"shipping_instructions"} className={"mb-0"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.shipping_instructions}</td>
                                    </tr>
                                ) : null}
                                {props.order.description ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"description"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.description}</td>
                                    </tr>
                                ) : null}
                                {props.showOrderUser ? (
                                    <tr>
                                        <th>
                                            <strong>
                                                <OwLabel title={"user"} />
                                            </strong>
                                        </th>
                                        <td>{props.order.User?.name}</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </div>

            {showDetailRow() ? (
                <div className={"details-row"}>
                    <table className={"table table-bordered " + tableTheme}>
                        <tbody>
                            <tr>
                                <th>
                                    <OwLabel title={"order_date"} className={"mb-0"} />
                                </th>
                                {supplierModel?.shipping_lead_time ? (
                                    <th>
                                        <OwLabel title={"est_ship_date"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                {supplierModel?.has_order_cancel_date ? (
                                    <th>
                                        <OwLabel title={"cancel_date"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                {!supplierModel?.has_est_ship_date ? (
                                    <th>
                                        <OwLabel title={"ship_date"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                {supplierModel?.has_do_not_ship_before_date ? (
                                    <th>
                                        <OwLabel title={"do_not_ship_before_date"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                <th>
                                    <OwLabel title={"order_no"} className={"mb-0"} />
                                </th>
                                {supplierModel?.has_terms_on_packing_list ? (
                                    <th>
                                        <OwLabel title={"terms"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                <th>
                                    <OwLabel title={"po_no"} className={"mb-0"} />
                                </th>
                                <th>
                                    <OwLabel title={"notes"} className={"mb-0"} />
                                </th>
                                {props.order.shipping_instructions ? (
                                    <th>
                                        <OwLabel title={"shipping_instructions"} className={"mb-0"} />
                                    </th>
                                ) : null}
                                {props.order.description ? (
                                    <th>
                                        <OwLabel title={"description"} />
                                    </th>
                                ) : null}
                                {props.showOrderUser ? (
                                    <th>
                                        <OwLabel title={"user"} />
                                    </th>
                                ) : null}
                            </tr>
                            <tr>
                                <td>{orderDate}</td>
                                {supplierModel?.shipping_lead_time ? <td>{estShipDate}</td> : null}
                                {supplierModel?.has_order_cancel_date ? <td>{props.order.cancel_date ? cancelDate : null}</td> : null}
                                {!supplierModel?.has_est_ship_date ? <td>{props.order.ship_date ? shippingDate : null}</td> : null}
                                {supplierModel?.has_do_not_ship_before_date ? (
                                    <td>{props.order.do_not_ship_before_date ? doNotShipBeforeDate : null}</td>
                                ) : null}
                                <td>{orderNums}</td>
                                {supplierModel?.has_terms_on_packing_list ? (
                                    <td>
                                        <OwLabel title={props.order.Vendor?.Payment_Type?.name.toLowerCase()} noBottomMargin={true} />
                                    </td>
                                ) : null}
                                <td>{poNums}</td>
                                <td>{notes}</td>
                                {props.order.shipping_instructions ? <td>{props.order.shipping_instructions}</td> : null}
                                {props.order.description ? <td>{props.order.description}</td> : null}
                                {props.showOrderUser ? <td>{props.order.User?.name}</td> : null}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : null}

            <div className={"items-row"}>
                <table className={"table table-bordered " + tableTheme}>
                    <tbody>
                        <tr>
                            <th className={thHeightFix ? "align-middle" : ""}>
                                <OwLabel title={"no"} className={"mb-0 "} />
                            </th>
                            {props.showItemLocation ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    {/*<OwLabel title={"location"} className={"mb-0"} />*/}
                                    <OwSortHeader
                                        arrowClassName={"d-print-none"}
                                        title={"location"}
                                        currentSortCol={sortCol}
                                        currentSortOrder={sortDir}
                                        sortFn={updateSortFn}
                                    />
                                </th>
                            ) : null}
                            <th className={thHeightFix ? "align-middle" : ""}>
                                <OwLabel title={"item"} className={"mb-0 "} />
                            </th>
                            {props.hideSkuCol ? null : (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    <OwLabel title={"sku"} className={"mb-0 "} />
                                </th>
                            )}
                            {props.showUom ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    <OwLabel title={"uom"} />
                                </th>
                            ) : null}
                            <th className={thHeightFix ? "align-middle" : ""}>
                                <OwLabel title={"description"} className={"mb-0 "} />
                            </th>
                            {supplierModel.has_lots ? (
                                <th className={thHeightFix ? "align-middle" : ""}>
                                    <OwLabel title={"lot"} className={"mb-0 " + (thHeightFix ? "mt-2" : "")} />
                                </th>
                            ) : null}
                            <th className={(props.showOrderQty ? "" : "d-none ") + (thHeightFix ? "align-middle" : "")}>
                                <OwLabel title={"order_qty"} className={"mb-0 " + (thHeightFix ? "mt-2" : "")} />
                            </th>
                            <th className={(props.showShipQty ? "" : "d-none ") + (thHeightFix ? "align-middle" : "")}>
                                <OwLabel title={"ship_qty"} className={"mb-0 "} />
                            </th>
                        </tr>
                        {orderItemRows}
                    </tbody>
                    <tbody>
                        <tr>
                            <td></td>
                            <td></td>
                            {props.showItemLocation ? <td></td> : null}
                            {props.hideSkuCol ? null : <td></td>}
                            {props.showUom ? <td></td> : null}
                            <td></td>
                            {supplierModel.has_lots ? <td></td> : null}
                            <td className={"w-15 " + (props.showOrderQty ? "" : "d-none")}>{totalQty}</td>
                            <td className={"w-15 " + (props.showShipQty ? "" : "d-none")}></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </OrderPackingListViewDiv>
    );
};

export default OrderPackingListView;

const OrderPackingListViewDiv = styled.div`
    display: flex;
    flex-direction: column;
`;
